import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Download, Copy, Send } from "lucide-react";
import {
  adminBookingDetail, adminApproveBooking, adminDeclineBooking, adminCancelBooking,
  adminResendPortalLink, adminRefundBooking, adminFetchBlob, extractApiError,
} from "@/lib/api";
import { SupplyQuoteFields, SupplyAdminPanel } from "@/components/SupplyAdmin";
import { RefundPanel } from "@/components/RefundPanel";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const Box = ({ title, children, testId }) => (
  <div className="p-5 border" style={{ borderColor: "var(--ss-border)" }} data-testid={testId}>
    <div className="font-label text-[0.6rem] mb-4" style={{ color: "var(--ss-rust)" }}>{title}</div>
    {children}
  </div>
);

const ApprovePanel = ({ d, token, onDone }) => {
  const q = d.booking.quote;
  const hasService = d.quote.has_service;
  const hasSupply = d.quote.has_supply;
  const sr = d.booking.supply_request || {};
  const executive = ["executive_luxe", "executive_lit"].includes(sr.package);
  const estLines = (sr.estimate?.lines || []).map((l) => ({
    product: l.quantity > 1 && !l.key.includes(":") ? `${l.label} (${l.quantity})` : l.label, quantity: 1,
    customization: "", unit_price: l.amount ?? "" }));
  const [sup, setSup] = useState({
    supply_items: q?.supply_items || (estLines.length ? estLines : [{ product: "", quantity: Number(sr.quantity) || 1, customization: sr.details || "", unit_price: "" }]),
    needed_by: q?.needed_by || sr.needed_by || "", fulfillment: q?.fulfillment || sr.fulfillment || "",
    pickup_location: q?.pickup_location || sr.pickup_location || "",
    delivery_address: q?.delivery_address || sr.delivery_address || sr.shipping_address || sr.event_address || "",
    delivery_state: q?.delivery_state || "", delivery_charge: q?.delivery_charge ?? (sr.fulfillment === "delivery" ? 10 : ""),
    shipping_charge: q?.fulfillment === "shipping" ? q.shipping_charge : "",
  });
  const [items, setItems] = useState(q?.line_items?.length ? q.line_items : [{ label: "", amount: "" }]);
  const [retainer, setRetainer] = useState(q?.retainer ?? d.settings.default_retainer ?? "");
  const [times, setTimes] = useState({ start: q?.start_time || "", end: q?.end_time || "" });
  const [note, setNote] = useState(q?.note || "");
  const [busy, setBusy] = useState(false);
  const total = hasService ? items.reduce((s, i) => s + (Number(i.amount) || 0), 0) : 0;
  const upd = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { note };
      if (hasService && !executive) Object.assign(payload, {
        line_items: items.filter((i) => i.label).map((i) => ({ label: i.label, amount: Number(i.amount) || 0 })),
        retainer: Number(retainer), start_time: times.start, end_time: times.end,
      });
      if (executive) Object.assign(payload, { start_time: times.start, end_time: times.end });
      if (hasSupply) Object.assign(payload, {
        ...sup, delivery_charge: Number(sup.delivery_charge) || 0,
        shipping_charge: sup.fulfillment === "shipping" && sup.shipping_charge !== "" ? Number(sup.shipping_charge) : null,
        supply_items: sup.supply_items.filter((i) => i.product).map((i) => ({ ...i, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 })),
      });
      await adminApproveBooking(token, d.booking.id, payload);
      toast.success("Approved — contract link emailed to client");
      onDone();
    } catch (e) { toast.error(extractApiError(e, "Could not approve")); }
    setBusy(false);
  };
  return (
    <Box title={q ? "Quote (editable until signed)" : "Approve & Send Quote"} testId="approve-panel">
      {hasService && executive && (
        <div data-testid="approve-executive">
          <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-muted)" }}>Executive Graduation Experience · 100% due at signing (no retainer or balance)</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label>Start time<input className="editorial-input" type="time" value={times.start} onChange={(e) => setTimes({ ...times, start: e.target.value })} data-testid="approve-start" /></label>
            <label>End time<input className="editorial-input" type="time" value={times.end} onChange={(e) => setTimes({ ...times, end: e.target.value })} data-testid="approve-end" /></label>
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--ss-muted)" }}>The package price and any add-ons are entered as Social Supply lines below. If the event is in the St. Louis area, add the $75 St. Louis travel fee as a line before approving — it is not added automatically.</p>
        </div>
      )}
      {hasService && !executive && <>
      <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-muted)" }}>Service · booking retainer + remaining balance</div>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input className="editorial-input flex-1 text-sm" placeholder="Package / add-on (e.g. Generator +$150)" value={it.label}
                 onChange={(e) => upd(i, "label", e.target.value)} data-testid={`approve-item-label-${i}`} />
          <input className="editorial-input text-sm" style={{ width: "7rem", flex: "none" }} type="number" step="0.01" placeholder="$" value={it.amount}
                 onChange={(e) => upd(i, "amount", e.target.value)} data-testid={`approve-item-amount-${i}`} />
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Remove line"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="font-label text-[0.6rem] inline-flex items-center gap-1 mb-4" onClick={() => setItems([...items, { label: "", amount: "" }])} data-testid="approve-add-item">
        <Plus size={12} /> Add line
      </button>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <label>Booking retainer ($)<input className="editorial-input" type="number" step="0.01" value={retainer} onChange={(e) => setRetainer(e.target.value)} data-testid="approve-retainer" /></label>
        <label>Start time<input className="editorial-input" type="time" value={times.start} onChange={(e) => setTimes({ ...times, start: e.target.value })} data-testid="approve-start" /></label>
        <label>End time<input className="editorial-input" type="time" value={times.end} onChange={(e) => setTimes({ ...times, end: e.target.value })} data-testid="approve-end" /></label>
      </div>
      <div className="mt-3 text-sm">Service total <strong data-testid="approve-total">{money(total)}</strong> · Remaining after retainer {money(total - (Number(retainer) || 0))} · Balance due 48h before start</div>
      </>}
      {hasSupply && <SupplyQuoteFields v={sup} setV={setSup} lead={d.supply_lead} />}
      <textarea className="editorial-input mt-3 text-sm" rows={2} placeholder="Optional note to client" value={note} onChange={(e) => setNote(e.target.value)} data-testid="approve-note" />
      <button className="btn-primary mt-4" disabled={busy || (hasService && (!times.start || !times.end || (!executive && (!retainer || total <= 0))))} onClick={submit} data-testid="approve-submit">
        {busy ? "Sending…" : q ? "Update & re-send quote" : "Approve & send contract"}
      </button>
    </Box>
  );
};

const ContractPanel = ({ d, token }) => {
  const sc = d.signed_contract;
  const [thumbs, setThumbs] = useState({});
  useEffect(() => {
    const urls = [];
    d.photos.forEach(async (p) => {
      try {
        const blob = await adminFetchBlob(token, `/admin/bookings/${d.booking.id}/photos/${p.id}`);
        const u = URL.createObjectURL(blob); urls.push(u);
        setThumbs((t) => ({ ...t, [p.id]: u }));
      } catch (e) { /* ignore */ }
    });
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [d.photos, d.booking.id, token]);
  const pdf = async () => {
    const blob = await adminFetchBlob(token, `/admin/bookings/${d.booking.id}/contract.pdf`);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `Simply-Social-Agreement-${d.booking.booking_ref}.pdf`; a.click();
  };
  const cf = sc?.client_fields || {};
  return (
    <Box title="Signed Contract" testId="contract-panel">
      {sc ? (
        <div className="text-sm space-y-1">
          <div>Signed by <strong>{sc.signer_name}</strong> · {new Date(sc.signed_at).toLocaleString()} · IP {sc.ip}</div>
          <div>Agreement v{sc.template_version} · {sc.signature_mode} signature · SHA-256 {sc.sha256.slice(0, 20)}…</div>
          <div>Home: {cf.home_address}{cf.dl_last4 && <> · DL last 4: <span data-testid="contract-dl4">{cf.dl_last4}</span></>}</div>
          {cf.event_street && <div>Event: {[cf.venue_name, cf.event_street, cf.event_city, cf.event_state, cf.event_zip].filter(Boolean).join(", ")} · {cf.indoor_outdoor} · {cf.guest_count} guests</div>}
          <div>Photo release: {cf.photo_release === "authorize" ? "Authorized" : "Not authorized"}</div>
          <button className="btn-outline mt-3 inline-flex items-center gap-2" onClick={pdf} data-testid="admin-download-pdf"><Download size={14} /> Download signed PDF</button>
        </div>
      ) : <div className="text-sm" style={{ color: "var(--ss-muted)" }}>Not signed yet.</div>}
      {d.photos.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-setup-photos">
          {d.photos.map((p) => thumbs[p.id]
            ? <a key={p.id} href={thumbs[p.id]} target="_blank" rel="noreferrer"><img src={thumbs[p.id]} alt={p.original_filename} className="h-24 w-24 object-cover border" /></a>
            : <span key={p.id} className="h-24 w-24 border flex items-center justify-center text-xs">Loading…</span>)}
        </div>
      )}
    </Box>
  );
};

const ClosePanel = ({ d, token, onDone }) => {
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const signed = !!d.booking.contract_id;
  const act = async () => {
    if (!confirming) { setConfirming(true); return; }
    setConfirming(false);
    try {
      await (signed ? adminCancelBooking : adminDeclineBooking)(token, d.booking.id, { reason, notify });
      toast.success(signed ? "Booking cancelled — no refund was issued automatically" : "Request declined");
      onDone();
    } catch (e) { toast.error(extractApiError(e)); }
  };
  return (
    <Box title={signed ? "Cancel Booking" : "Decline Request"} testId="close-panel">
      <textarea className="editorial-input text-sm" rows={2} placeholder="Reason (shared with client if notified)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="close-reason" />
      <label className="flex gap-2 items-center text-sm mt-2"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Email the client</label>
      <button className="btn-outline mt-3" onClick={act} data-testid="close-submit">{confirming ? (signed ? "Confirm: cancel booking & release date" : "Confirm: decline request") : signed ? "Cancel booking" : "Decline request"}</button>
      {confirming && <button className="ml-3 text-xs underline" onClick={() => setConfirming(false)} data-testid="close-back">Back</button>}
    </Box>
  );
};

export const BookingWorkflowModal = ({ bookingId, token, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const load = async () => {
    try { setD(await adminBookingDetail(token, bookingId)); } catch (e) { toast.error(extractApiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bookingId]);
  const refresh = () => { load(); onChanged(); };
  const resend = async () => {
    try { await adminResendPortalLink(token, bookingId); toast.success("Portal link emailed"); } catch (e) { toast.error(extractApiError(e)); }
  };
  const b = d?.booking;
  const closed = b && ["cancelled", "declined", "completed"].includes(b.status);
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 md:p-10" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="workflow-modal">
      <div className="w-full max-w-3xl p-6 md:p-8 space-y-5" style={{ background: "var(--ss-bg)", color: "var(--ss-cream)" }}>
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }} data-testid="workflow-ref">{b?.booking_ref || "…"}</div>
            <div className="font-display text-3xl">{b?.name}</div>
            {b && <div className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>{b.event_date} · {b.event_location} · status <strong data-testid="workflow-status">{b.status}</strong></div>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="workflow-close"><X size={20} /></button>
        </div>
        {d && (
          <>
            <div className="flex flex-wrap gap-2">
              <button className="btn-outline inline-flex items-center gap-2" onClick={() => { navigator.clipboard?.writeText(d.portal_url); toast.success("Portal link copied"); }} data-testid="workflow-copy-link"><Copy size={14} /> Copy client portal link</button>
              <button className="btn-outline inline-flex items-center gap-2" onClick={resend} data-testid="workflow-resend-link"><Send size={14} /> Email portal link</button>
            </div>
            {!b.contract_id && !closed && <ApprovePanel d={d} token={token} onDone={refresh} />}
            <ContractPanel d={d} token={token} />
            {d.quote.has_supply && <SupplyAdminPanel d={d} token={token} onDone={refresh} />}
            <Box title="Payments" testId="payments-panel">
              <div className="text-sm space-y-1">
                {(b.payments || []).map((p, i) => <div key={i}>{{ retainer: d.quote.executive ? "Executive package (paid in full)" : "Booking retainer", supply: "Social Supply (paid in full)", balance: "Balance", revision: "Additional revision round" }[p.kind]}{p.duplicate && " (DUPLICATE)"} · {money(p.amount)}{p.kind === "retainer" && p.supply_amount > 0 && ` (incl. Social Supply ${money(p.supply_amount)})`} · {new Date(p.paid_at).toLocaleString()}</div>)}
                {(b.refunds || []).map((r, i) => <div key={`r${i}`} style={{ color: "var(--ss-muted)" }}>Refund −{money(r.amount)} · {new Date(r.at).toLocaleString()}</div>)}
                {d.quote.total > 0 && <div className="pt-2">Total {money(d.quote.total)} · Paid {money(d.quote.amount_paid)} · Remaining <strong data-testid="workflow-remaining">{money(d.quote.amount_remaining)}</strong>{d.quote.balance_due_display && ` · due ${d.quote.balance_due_display}`}</div>}
                {!(b.payments || []).length && <div style={{ color: "var(--ss-muted)" }}>No payments yet.</div>}
              </div>
            </Box>
            {d.refund.total_paid > 0 && <RefundPanel d={d} token={token} onDone={refresh} />}
            {!closed && <ClosePanel d={d} token={token} onDone={refresh} />}
            <Box title="Timeline" testId="timeline-panel">
              <ol className="text-xs space-y-1" style={{ color: "var(--ss-cream-dim)" }}>
                {[...(b.history || [])].reverse().map((e, i) => (
                  <li key={i}>{new Date(e.at).toLocaleString()} — <strong>{e.action.replace(/_/g, " ")}</strong>{e.note && ` · ${e.note}`} <span style={{ color: "var(--ss-muted)" }}>({e.by})</span></li>
                ))}
              </ol>
            </Box>
          </>
        )}
      </div>
    </div>
  );
};
